/**
 * Sales territories (M8) — lasso and automated creator. Both produce the same
 * `Territory` shape (id, name, color, polygon, memberRowIds, rollup) that
 * persists in `analysis.territories`; boundary mode lives in ./boundary.ts but
 * emits the same shape. Automated territories are hand-editable afterward via
 * lasso, so the two modes interoperate through one representation.
 */

import type { Point, PointSet } from "@/lib/map/pointset";
import { convexHull, pointsInRing, type Ring } from "./geometry";
import { balancedPartition } from "./partition";
import type { Territory, TerritoryRollup } from "./spec";

let territorySeq = 0;
function territoryId(): string {
  territorySeq += 1;
  return `terr_${Date.now().toString(36)}_${territorySeq}`;
}

/** Pick the palette color for the i-th territory (wraps past the palette length). */
export function territoryColor(palette: string[], index: number): string {
  if (palette.length === 0) return "";
  return palette[index % palette.length]!;
}

/** Roll a member set up to a count (+ numeric sum when balancing by a column). */
export function rollupOf(members: Point[], balanceBy?: string): TerritoryRollup {
  const rollup: TerritoryRollup = { count: members.length };
  if (balanceBy) {
    let sum = 0;
    for (const p of members) {
      const n = Number(p.props[balanceBy]);
      if (Number.isFinite(n)) sum += n;
    }
    rollup.sum = sum;
    rollup.balanceBy = balanceBy;
  }
  return rollup;
}

/**
 * Lasso: enclose points in a freehand polygon → a named, recolored territory
 * with the correct `memberRowIds`. The drawn ring persists as the polygon so
 * the overlay renders the exact shape the user drew.
 */
export function buildLassoTerritory(
  pointSet: PointSet,
  ring: Ring,
  opts: { name: string; color: string; id?: string },
): Territory {
  const memberRowIds = pointsInRing(pointSet.points, ring);
  const members = pointSet.points.filter((p) => memberRowIds.includes(p.id));
  return {
    id: opts.id ?? territoryId(),
    name: opts.name,
    color: opts.color,
    polygon: ring,
    memberRowIds,
    rollup: rollupOf(members),
  };
}

export interface AutomatedOptions {
  /** Target territory count. */
  count: number;
  /** Numeric column to balance by; when absent, balance by point count. */
  balanceBy?: string;
  /** Palette to color territories from (token-sourced). */
  palette: string[];
  /** Name prefix ("Territory 1", "Territory 2", …). */
  namePrefix?: string;
}

/**
 * Automated creator: partition points into `count` roughly balanced territories
 * (by count, or by a numeric column). Each territory gets a convex-hull polygon
 * for rendering, the balanced `memberRowIds`, and a rollup. Re-runnable
 * (deterministic) and hand-editable via lasso afterward. Empty clusters are
 * dropped so `count` is a target, never a guarantee of non-empty territories.
 */
export function buildAutomatedTerritories(pointSet: PointSet, opts: AutomatedOptions): Territory[] {
  const { assignment } = balancedPartition(pointSet.points, {
    count: opts.count,
    balanceBy: opts.balanceBy,
  });
  const buckets = new Map<number, Point[]>();
  pointSet.points.forEach((p, i) => {
    const c = assignment[i]!;
    (buckets.get(c) ?? buckets.set(c, []).get(c)!).push(p);
  });
  const prefix = opts.namePrefix ?? "Territory";
  const territories: Territory[] = [];
  let n = 0;
  for (const members of [...buckets.entries()].sort((a, b) => a[0] - b[0]).map((e) => e[1])) {
    if (members.length === 0) continue;
    n += 1;
    const hull = convexHull(members);
    territories.push({
      id: territoryId(),
      name: `${prefix} ${n}`,
      color: territoryColor(opts.palette, n - 1),
      polygon: hull.length >= 4 ? hull : undefined,
      memberRowIds: members.map((p) => p.id),
      rollup: rollupOf(members, opts.balanceBy),
    });
  }
  return territories;
}

/** Recompute a lasso territory's members against a (possibly new) point set. */
export function recomputeLassoTerritory(territory: Territory, pointSet: PointSet): Territory {
  if (!territory.polygon) return territory;
  const memberRowIds = pointsInRing(pointSet.points, territory.polygon);
  const members = pointSet.points.filter((p) => memberRowIds.includes(p.id));
  return { ...territory, memberRowIds, rollup: rollupOf(members, territory.rollup?.balanceBy) };
}
